// One compact row in the owner Devices inventory. Primary identity, source,
// reporting state, and the manual action stay visible; forensic NMEA fields
// remain behind an explicit Details control.

import { Fragment, useId, useState } from 'react';
import { Link } from 'react-router-dom';

import { deviceCardChannels } from '../../lib/deviceCardChannels';
import { deviceIdentityName, deviceSourceName } from '../../lib/deviceInventory';
import { seenStatus } from '../../lib/deviceSeen';
import { findRicherTwin } from '../../lib/deviceTwins';
import type { VesselDevice } from '../../lib/useBoatDevices';
import { useBoatChrome } from '../app/boatChrome';
import { ChevronRightIcon } from '../app/icons';

function manualAffordance(device: VesselDevice, openManual: (device: VesselDevice) => void) {
  const manuals = device.manuals ?? [];
  const first = manuals[0];
  const name = device.preferredName ?? device.displayName;

  if (manuals.length === 0) {
    return (
      <span
        className="no-manual-link"
        title="No device manual published for this model yet — availability does not depend on the device being online"
      >
        No manual yet
      </span>
    );
  }
  if (manuals.length === 1 && first) {
    return (
      <a
        className="manual-link"
        href={first.downloadUrl}
        target="_blank"
        rel="noreferrer"
        data-testid={`open-manual-${device.deviceId}`}
        aria-label={`Open manual for ${name}`}
      >
        Open manual <ChevronRightIcon className="chev" />
      </a>
    );
  }
  return (
    <button
      type="button"
      className="manual-link"
      data-testid={`open-manual-${device.deviceId}`}
      aria-label={`Open manuals for ${name}`}
      onClick={() => openManual(device)}
    >
      Open manuals <span className="count">{manuals.length}</span>{' '}
      <ChevronRightIcon className="chev" />
    </button>
  );
}

function protocolLabel(protocol: VesselDevice['protocol']): string {
  if (protocol === 'n2k') return 'NMEA 2000 device';
  if (protocol === 'n0183') return 'NMEA 0183 device';
  if (protocol === 'venus') return 'Victron device';
  if (protocol === 'starlink') return 'Starlink dish';
  return 'Signal K source';
}

function reportingLabel(state: ReturnType<typeof seenStatus>['state']): string {
  if (state === 'fresh') return 'Reporting';
  if (state === 'stale') return 'Quiet';
  if (state === 'cold') return 'Offline';
  return 'No data';
}

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function DeviceTechnicalDetails({
  device,
  boatId,
  detailsId,
}: {
  device: VesselDevice;
  boatId: string;
  detailsId: string;
}) {
  const { devices } = useBoatChrome();
  const cfg = device.configurationInfo;
  const source = deviceSourceName(device, { includeConnection: true });
  const channels = deviceCardChannels(device);
  // Searched over the whole boat inventory, not the filtered list this section
  // was handed: the twin often lives behind a different gateway, and a gateway
  // filter must not be able to hide the explanation for the empty row.
  const inventory = devices.state.kind === 'ready' ? devices.state.devices : [];
  const richerTwin = findRicherTwin(device, inventory);

  return (
    <div
      id={detailsId}
      className="tech-body open tech-body--list"
      data-testid={`device-details-${device.deviceId}`}
    >
      {/* Channels lead the expansion. On a SIM100/RIM100 "Engine Room Bilge
          High Water" is the most human-meaningful thing on the card and CAN
          NAME the least, so the channel list sits above the raw fields rather
          than below them — and above the "Open full device record" link, which
          is what a user had to hunt for before to see channels at all. Renders
          nothing when the device reports none, which is nearly every device on
          a boat.

          Its own <dl> purely so the block can carry a testid; it inherits the
          same .tech-body grid as the fields below, so it adds no CSS and stays
          compact. The two grids would otherwise butt together with no gap at
          all (.tech-body dl is margin: 0) and read as one run-on list, so the
          seam gets a hair more than the 7px row-gap inside each grid — the same
          inline-spacing idiom the detail page's Channels panel already uses. */}
      {channels.rows.length > 0 ? (
        <dl
          aria-label="Channels"
          data-testid={`device-card-channels-${device.deviceId}`}
          style={{ marginBottom: 10 }}
        >
          {channels.rows.map((channel) => (
            <Fragment key={channel.channel}>
              <dt>Channel {channel.channel}</dt>
              <dd>{channel.label}</dd>
            </Fragment>
          ))}
          {channels.overflowNote ? (
            <>
              <dt>More</dt>
              <dd>{channels.overflowNote}</dd>
            </>
          ) : null}
        </dl>
      ) : null}

      {/* Stands where the channel list would have been. On a boat running both
          a native N2K gateway and Signal K, one physical module is listed
          twice and only one of the two rows reports the channel names — this
          is the other half of a pair, not a broken device. The rows stay
          deliberately unmerged (core `wireDeviceId`); one quiet line names the
          sibling and links to it. Absent for every device without a richer
          twin, which is nearly all of them. */}
      {channels.rows.length === 0 && richerTwin ? (
        <p
          className="muted"
          style={{ margin: '0 0 10px' }}
          data-testid={`device-card-twin-${device.deviceId}`}
        >
          Same device as{' '}
          <Link
            to={`/dashboard/boats/${boatId}/devices/${encodeURIComponent(richerTwin.deviceId)}`}
            data-testid={`device-card-twin-link-${device.deviceId}`}
          >
            {richerTwin.displayName}
          </Link>{' '}
          — channel names are reported there.
        </p>
      ) : null}

      <dl>
        <dt>Protocol</dt>
        <dd>{protocolLabel(device.protocol)}</dd>
        <dt>Device identity</dt>
        <dd>{deviceIdentityName(device)}</dd>
        <dt>Gateway</dt>
        <dd>{source}</dd>
        {device.sourceId ? (
          <>
            <dt>Internal gateway ID</dt>
            <dd className="mono tech-raw-id">{device.sourceId}</dd>
          </>
        ) : null}
        {device.sourceLabel ? (
          <>
            <dt>Signal K label</dt>
            <dd>{device.sourceLabel}</dd>
          </>
        ) : null}
        {device.srcAddress !== undefined ? (
          <>
            <dt>Source address</dt>
            <dd className="mono">{device.srcAddress}</dd>
          </>
        ) : null}
        {device.productInfo?.serialCode ? (
          <>
            <dt>Serial</dt>
            <dd>{device.productInfo.serialCode}</dd>
          </>
        ) : null}
        {device.productInfo?.softwareVersion ? (
          <>
            <dt>Software</dt>
            <dd>{device.productInfo.softwareVersion}</dd>
          </>
        ) : null}
        {device.deviceClass?.name ? (
          <>
            <dt>Class</dt>
            <dd>{device.deviceClass.name}</dd>
          </>
        ) : null}
        {device.deviceFunction?.name ? (
          <>
            <dt>Function</dt>
            <dd>{device.deviceFunction.name}</dd>
          </>
        ) : null}
        {cfg?.installationDescription1 ? (
          <>
            <dt>Installation</dt>
            <dd>{cfg.installationDescription1}</dd>
          </>
        ) : null}
        {cfg?.installationDescription2 ? (
          <>
            <dt>Installation 2</dt>
            <dd>{cfg.installationDescription2}</dd>
          </>
        ) : null}
        {device.transmitPgns && device.transmitPgns.length > 0 ? (
          <>
            <dt>Transmits</dt>
            <dd className="mono">{device.transmitPgns.join(', ')}</dd>
          </>
        ) : null}
        {device.receivePgns && device.receivePgns.length > 0 ? (
          <>
            <dt>Receives</dt>
            <dd className="mono">{device.receivePgns.join(', ')}</dd>
          </>
        ) : null}
        {device.canName ? (
          <>
            <dt>CAN NAME</dt>
            <dd className="mono">{device.canName}</dd>
          </>
        ) : null}
        <dt>First seen</dt>
        <dd>
          <time dateTime={device.firstSeen}>{formatTimestamp(device.firstSeen)}</time>
        </dd>
        <dt>Internal ID</dt>
        <dd className="mono tech-raw-id">{device.deviceId}</dd>
      </dl>
      <p className="tech-detail-link">
        <Link to={`/dashboard/boats/${boatId}/devices/${encodeURIComponent(device.deviceId)}`}>
          Open full device record →
        </Link>
      </p>
    </div>
  );
}

export function DeviceCard({
  device,
  boatId,
  now = Date.now(),
  twins,
}: {
  device: VesselDevice;
  boatId: string;
  now?: number;
  /**
   * Other protocol views of this same physical instrument, grouped under
   * this lead row (lib/deviceGrouping, task sk_rows_unfolded). Rendered as
   * one quiet alias line each — the rows stay unmerged in the data, per the
   * July sk_device_duplicates decision; only the presentation nests.
   */
  twins?: VesselDevice[];
}) {
  const { openManual } = useBoatChrome();
  const [techOpen, setTechOpen] = useState(false);
  const detailsId = useId();

  const seen = seenStatus(device.lastSeen, now);
  const needsAttention = seen.state === 'cold' || seen.state === 'never';
  const manufacturer = device.manufacturer?.name ?? '';
  const model = device.productInfo?.modelId ?? '';
  const metaTop = [manufacturer, model].filter(Boolean).join(' · ');
  const role = device.deviceFunction?.name ?? device.deviceClass?.name ?? '';

  return (
    <article
      className={`device-card--list${needsAttention ? ' needs-attention' : ''}`}
      data-testid={`device-row-${device.deviceId}`}
    >
      <div className="device-list-row">
        <div className="device-list-identity">
          <div className="device-list-identity-copy">
            <span className="dc-name">{device.displayName}</span>
            <span className="dc-meta">{metaTop || protocolLabel(device.protocol)}</span>
          </div>
        </div>

        <div className="device-list-context">
          <span className={`device-list-role${role ? '' : ' faint'}`}>
            <span className="sr-only">Role: </span>
            {role || 'Role unknown'}
          </span>
          <span
            className="device-list-source"
            title={deviceSourceName(device, { includeConnection: true })}
          >
            <span className="sr-only">Gateway: </span>
            {deviceSourceName(device)}
          </span>
        </div>

        <span
          className={`device-list-status device-list-status--${seen.state}`}
          aria-label={seen.status}
        >
          <span className={`seen-dot seen-dot--${seen.state}`} aria-hidden="true" />
          <span className="device-list-status-copy">
            <strong>{reportingLabel(seen.state)}</strong>
            {seen.state === 'never' ? null : <span>{seen.label}</span>}
          </span>
        </span>

        <button
          type="button"
          className="device-list-toggle"
          aria-expanded={techOpen}
          aria-controls={techOpen ? detailsId : undefined}
          aria-label={`${techOpen ? 'Hide' : 'Show'} details for ${device.displayName}`}
          data-testid={`device-details-toggle-${device.deviceId}`}
          onClick={() => setTechOpen((open) => !open)}
        >
          <span>Details</span>
          <ChevronRightIcon />
        </button>

        <span className="device-list-manual">{manualAffordance(device, openManual)}</span>
      </div>

      {twins && twins.length > 0 ? (
        <p className="muted device-list-aliases" data-testid={`device-aliases-${device.deviceId}`}>
          Also listed via{' '}
          {twins.map((twin, i) => (
            <Fragment key={twin.deviceId}>
              {i > 0 ? ' and ' : null}
              <Link
                to={`/dashboard/boats/${boatId}/devices/${encodeURIComponent(twin.deviceId)}`}
                data-testid={`device-alias-link-${twin.deviceId}`}
              >
                {deviceSourceName(twin)}
              </Link>
            </Fragment>
          ))}{' '}
          — same physical device.
        </p>
      ) : null}

      {techOpen ? (
        <DeviceTechnicalDetails device={device} boatId={boatId} detailsId={detailsId} />
      ) : null}
    </article>
  );
}
